import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageContainer } from "@/layouts/PageContainer";
import { CreditCard, ChevronLeft, AlertCircle, User, Plus, X, ExternalLink } from "lucide-react";

const nativeSelect =
  "h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground disabled:opacity-50";

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function SeatManagement() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { getToken } = useAuth();

  const [school, setSchool] = useState(null);
  const [schoolSub, setSchoolSub] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddSeats, setShowAddSeats] = useState(false);
  const [addCount, setAddCount] = useState(1);
  const [adding, setAdding] = useState(false);

  const [actionInProgress, setActionInProgress] = useState(null);

  const [purchaseCount, setPurchaseCount] = useState(5);
  const [checkingOut, setCheckingOut] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, membersRes, subRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/members`, { headers }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/subscription`, { headers }),
      ]);
      if (!schoolRes.ok) {
        const body = await schoolRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load school");
      }
      setSchool(await schoolRes.json());
      if (membersRes.ok) setMembers(await membersRes.json());
      if (subRes.ok) setSchoolSub(await subRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const assignedUserIds = new Set(
    (schoolSub?.seats ?? [])
      .map((s) => s.userId)
      .filter(Boolean)
      .map(String)
  );

  const handleAssign = async (idx, userId) => {
    const key = userId ? `assign-${idx}` : `unassign-${idx}`;
    setActionInProgress(key);
    try {
      const token = await getToken();
      const method = userId ? "PUT" : "DELETE";
      const opts = { method, headers: { Authorization: `Bearer ${token}` } };
      if (userId) {
        opts.headers["Content-Type"] = "application/json";
        opts.body = JSON.stringify({ userId });
      }
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/seats/${idx}/assign`,
        opts
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to update seat");
      }
      const data = await res.json();
      setSchoolSub((prev) => ({ ...prev, seats: data.seats }));
      toast.success(userId ? "Seat assigned." : "Seat unassigned.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleAddSeats = async (e) => {
    e.preventDefault();
    const count = Number(addCount);
    if (!count || count < 1) return;
    setAdding(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/seats/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ count }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to add seats");
      }
      const data = await res.json();
      setSchoolSub((prev) => ({ ...prev, seats: data.seats }));
      setAddCount(1);
      setShowAddSeats(false);
      toast.success(`${count} seat${count !== 1 ? "s" : ""} added.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    const count = Number(purchaseCount);
    if (!count || count < 1) return;
    setCheckingOut(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ seatCount: count }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to create checkout session");
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      toast.error(err.message);
      setCheckingOut(false);
    }
  };

  const openBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/billing-portal`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to open billing portal");
      }
      const { url } = await res.json();
      window.open(url, "_blank");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setOpeningPortal(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </PageContainer>
    );
  }

  const seats = schoolSub?.seats ?? [];
  const hasSubscription =
    !!schoolSub?.stripeSubscriptionId && schoolSub?.status !== "none";
  const status = schoolSub?.status;

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        {school?.name}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <CreditCard size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-normal text-foreground">
            Teacher Accounts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Assign teacher licenses and manage your school&apos;s billing.
          </p>
        </div>
      </div>

      {!hasSubscription ? (
        <div className="max-w-[480px]">
          <div className="bg-card border border-border rounded-2xl px-6 py-6">
            <h2 className="text-sm font-semibold text-foreground mb-1">
              Purchase Teacher Licenses
            </h2>
            <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
              Each license gives one teacher access to the School plan — 120 reports and 200
              AI generations per year. Billed annually through Stripe.
            </p>
            <form onSubmit={handlePurchase} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Number of Teachers</Label>
                <Input
                  type="number"
                  min={1}
                  value={purchaseCount}
                  onChange={(e) => setPurchaseCount(e.target.value)}
                  className="h-10 max-w-[160px]"
                  required
                />
              </div>
              <Button type="submit" disabled={checkingOut || !purchaseCount}>
                {checkingOut ? "Redirecting to Stripe..." : "Purchase Seats"}
              </Button>
            </form>
          </div>
        </div>
      ) : (
        <>
          {/* Subscription status bar */}
          <div className="bg-card border border-border rounded-xl px-5 py-4 mb-6 flex flex-wrap items-center gap-x-6 gap-y-2">
            <div>
              <p className="text-xs text-muted-foreground font-medium">Status</p>
              <span
                className={`inline-block mt-0.5 text-sm font-semibold ${
                  status === "active"
                    ? "text-green-600 dark:text-green-400"
                    : status === "past_due"
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-muted-foreground"
                }`}
              >
                {status === "active"
                  ? "Active"
                  : status === "past_due"
                  ? "Past Due"
                  : status === "cancelled"
                  ? "Cancelled"
                  : status ?? "-"}
              </span>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Teachers</p>
              <p className="text-sm font-semibold text-foreground mt-0.5">
                {seats.length}
              </p>
            </div>
            {schoolSub?.currentPeriodEnd && (
              <div>
                <p className="text-xs text-muted-foreground font-medium">Renews</p>
                <p className="text-sm font-semibold text-foreground mt-0.5">
                  {formatDate(schoolSub.currentPeriodEnd)}
                </p>
              </div>
            )}
            <div className="ml-auto">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 h-8"
                onClick={openBillingPortal}
                disabled={openingPortal}
              >
                <ExternalLink size={13} />
                {openingPortal ? "Opening..." : "Billing Portal"}
              </Button>
            </div>
          </div>

          {/* Seat grid */}
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground">
              Teachers ({seats.length})
            </h2>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 h-8"
              onClick={() => setShowAddSeats((v) => !v)}
            >
              <Plus size={13} />
              Add Teachers
            </Button>
          </div>

          {showAddSeats && (
            <form
              onSubmit={handleAddSeats}
              className="bg-card border border-border rounded-xl px-4 py-4 mb-3 flex items-end gap-3 flex-wrap"
            >
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">How many to add?</Label>
                <Input
                  type="number"
                  min={1}
                  value={addCount}
                  onChange={(e) => setAddCount(e.target.value)}
                  className="h-9 w-24"
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={adding} className="h-9">
                  {adding ? "Adding..." : "Add"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0"
                  onClick={() => setShowAddSeats(false)}
                >
                  <X size={14} />
                </Button>
              </div>
            </form>
          )}

          {seats.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center rounded-2xl border border-dashed border-border bg-muted/20 mb-6">
              <CreditCard size={28} className="text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No teachers assigned yet</p>
            </div>
          ) : (
            <div className="space-y-2 mb-8">
              {seats.map((seat, idx) => {
                const assignBusy =
                  actionInProgress === `assign-${idx}` ||
                  actionInProgress === `unassign-${idx}`;
                const assignedId = seat.userId ? String(seat.userId) : "";

                const availableForAssign = members.filter((m) => {
                  const mid = String(m.userId);
                  return !assignedUserIds.has(mid) || mid === assignedId;
                });

                return (
                  <div
                    key={idx}
                    className="bg-card border border-border rounded-xl px-5 py-4"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <User size={14} className="text-muted-foreground shrink-0" />
                      <span className="text-sm font-semibold text-foreground">
                        Seat {idx + 1}
                      </span>
                    </div>
                    <div className="space-y-1.5 max-w-[280px]">
                      <p className="text-xs text-muted-foreground font-medium">
                        Assigned Teacher
                      </p>
                      <select
                        className={nativeSelect}
                        value={assignedId}
                        disabled={assignBusy}
                        onChange={(e) => handleAssign(idx, e.target.value)}
                      >
                        <option value="">- Unassigned -</option>
                        {availableForAssign.map((m) => (
                          <option key={String(m.userId)} value={String(m.userId)}>
                            {m.name || m.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
